import ScrollReveal from './ScrollReveal';
import { useTranslation } from 'react-i18next';
import { TypeAnimation } from 'react-type-animation';
import { NavLink } from 'react-router-dom';

import Logo from 'assets/logo2.svg';

const Hero = () => {
  const { t, i18n } = useTranslation();

  const typing = t('hero.typing', {
    returnObjects: true,
  });

  const sequence = [];

  typing.forEach((text) => {
    sequence.push(text);
    sequence.push(2000);
  });

  return (
    <section
      className='
        relative
        flex
        items-center
        justify-center

        min-h-[calc(100vh-64px)]

        px-6
        md:px-8

        overflow-hidden
        bg-white
      '
    >
      {/* ================= BACKGROUND LIGHT ================= */}

      <div className='absolute inset-0 flex items-center justify-center pointer-events-none'>
        <div
          className='
            w-[420px]
            h-[420px]

            sm:w-[560px]
            sm:h-[560px]

            md:w-[720px]
            md:h-[720px]

            rounded-full

            bg-gradient-to-br
            from-[#FFE6E6]
            via-transparent
            to-[#FFF1DC]

            blur-[90px]
            opacity-70
          '
        />
      </div>

      {/* subtle radial */}

      <div
        className='
          absolute
          inset-0

          bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.6),transparent_60%)]
          pointer-events-none
        '
      />

      <div className='relative w-full mx-auto max-w-7xl'>
        <ScrollReveal>
          <div className='flex flex-col items-center justify-center w-full py-24 md:py-32'>
            {/* ================= GRID ================= */}

            <div
              className='
                flex
                flex-col

                lg:grid
                lg:grid-cols-[1fr_auto_1fr]

                items-center

                gap-10
                md:gap-14
                lg:gap-8
              '
            >
              {/* ================= LEFT ================= */}

              <div
                className='
                  text-center
                  lg:text-right

                  max-w-[460px]
                  mx-auto
                  lg:ml-auto
                '
              >
                <h1
                  className='
                    leading-tight

                    text-[30px]
                    sm:text-[40px]
                    md:text-[46px]
                    lg:text-[52px]

                    font-semibold
                    tracking-tight

                    text-neutral-900
                  '
                >
                  <TypeAnimation
                    key={`left-${i18n.language}`}
                    sequence={[t('hero.title1'), 1500]}
                    wrapper='span'
                    cursor={false}
                    repeat={Infinity}
                    speed={40}
                  />
                </h1>

                <p className='mt-6 text-sm leading-relaxed md:text-base text-neutral-500'>
                  We craft brands, digital products, and experiences that feel
                  modern, refined, and memorable.
                </p>
              </div>

              {/* ================= CENTER LOGO ================= */}

              <div className='relative flex flex-col items-center'>
                {/* glow */}

                <div
                  className='
                    absolute

                    w-[220px]
                    h-[220px]

                    sm:w-[280px]
                    sm:h-[280px]

                    md:w-[340px]
                    md:h-[340px]

                    rounded-full

                    bg-gradient-to-br
                    from-[#FFB3B3]
                    via-[#FFD8A8]
                    to-transparent

                    opacity-70

                    blur-[70px]

                    animate-pulse

                    pointer-events-none
                  '
                />

                {/* logo */}

                <img
                  src={Logo}
                  alt='logo'
                  className='
                    relative

                    w-48
                    sm:w-60
                    md:w-72
                    lg:w-80

                    select-none

                    drop-shadow-[0_25px_45px_rgba(0,0,0,0.15)]
                  '
                />

                <p
                  className='
                    mt-6

                    text-[10px]
                    sm:text-xs
                    md:text-sm

                    tracking-[0.3em]

                    uppercase
                    font-medium

                    text-neutral-400
                  '
                >
                  Design & Development Studio
                </p>
              </div>

              {/* ================= RIGHT ================= */}

              <div
                className='
                  text-center
                  lg:text-left
                   min-w-[300px]
                  max-w-[460px]
                  mx-auto
                  lg:mr-auto
                '
              >
                <h1
                  className='font-semibold leading-tight tracking-tight  hero-text text-neutral-900'
                >
                  <TypeAnimation
                    key={`right-${i18n.language}`}
                    sequence={sequence}
                    wrapper='span'
                    cursor
                    repeat={Infinity}
                    speed={40}
                  />
                </h1>

                {/* CTA */}

                <div className='flex flex-col items-center justify-center gap-4 mt-8 sm:flex-row lg:justify-start'>
                  <NavLink
                    to='/portfolio/graphic'
                    className='px-6 py-3 text-sm font-medium text-white transition bg-black rounded-full hover:bg-neutral-800'
                  >
                    Explore Work
                  </NavLink>
                </div>
              </div>
            </div>
          </div>
        </ScrollReveal>
      </div>

      {/* ================= SCROLL INDICATOR ================= */}

      <div className='absolute flex flex-col items-center gap-2 bottom-10 text-neutral-400 animate-bounce'>
        <div
          className='
            w-[18px]
            h-[28px]

            border
            border-neutral-400

            rounded-full

            flex
            items-start
            justify-center

            p-[3px]
          '
        >
          <div
            className='
              w-[3px]
              h-[6px]

              bg-neutral-400

              rounded-full

              animate-pulse
            '
          />
        </div>

        <span
          className='
            text-[10px]
            sm:text-xs

            tracking-widest

            uppercase

            opacity-70
          '
        >
          Scroll
        </span>
      </div>
    </section>
  );
};

export default Hero;
